import { useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarOff, ChevronDown, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isMemberAnAdmin, isMemberAPracticeManager } from "@/core/auth";
import { useSetRecurrenceException } from "@/hooks/useSetRecurrenceException";
import { isCancellationChargeable } from "@/utils/cancellationCharge";
import { cn } from "@/lib/utils";
import type { Booking } from "@/types/Booking";
import type { Member } from "@/types/Member";
import { RecurrenceExceptionConfirmDialog } from "./RecurrenceExceptionConfirmDialog";
import { generateOccurrenceList, type OccurrenceEntry } from "./generateOccurrenceList";

interface RecurrenceExceptionsManagerProps {
  booking: Booking;
  member: Member | null;
  onMutated?: () => void;
  // Optional controlled open state for the accordion. When omitted the
  // Collapsible manages its own state (uncontrolled). ViewBooking passes these
  // so the "Cancel occurrences" button can expand the accordion programmatically.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const stateBadgeClasses: Record<OccurrenceEntry["state"], string> = {
  available: "bg-emerald-100 text-emerald-800",
  excluded: "bg-amber-100 text-amber-800",
  past: "bg-gray-100 text-gray-500",
};

const stateLabel: Record<OccurrenceEntry["state"], string> = {
  available: "Available",
  excluded: "Excluded",
  past: "Past",
};

export function RecurrenceExceptionsManager({ booking, member, onMutated, open, onOpenChange }: RecurrenceExceptionsManagerProps) {
  const isElevated =
    isMemberAnAdmin(member) || isMemberAPracticeManager(member);
  // R19/R20: members may exclude their own non-slot recurrent occurrences. Slot
  // bookings remain elevated-only — slot holidays go through SlotHolidayDialog.
  const isOwnerOfNonSlot =
    !!member && booking.memberId === member.id && booking.type !== "slot";
  const canManage = isElevated || isOwnerOfNonSlot;

  const recurrence = booking.recurrence ?? booking.recurrencePattern ?? null;
  const baseBooking = booking.recurrencePattern ? booking : booking;
  const [openEntry, setOpenEntry] = useState<OccurrenceEntry | null>(null);
  const [waive, setWaive] = useState(false);
  const mutation = useSetRecurrenceException();

  // C1 — excluding an occurrence within 24h of its start is charged exactly like a late cancellation
  // (the backend writes a Cancellation record). Warn before confirming; elevated users may waive.
  // Only the "exclude" (add) direction can be charged; re-including a past-excluded date never is.
  const willBeCharged = useMemo(
    () =>
      !!openEntry &&
      openEntry.state === "available" &&
      booking.type !== "slot" && // slots never accrue cancellation charges (C11)
      isCancellationChargeable(booking, { fromDate: openEntry.date }).chargeable,
    [openEntry, booking],
  );

  const entries = useMemo(() => {
    if (!recurrence) return [];
    return generateOccurrenceList(new Date(booking.date), recurrence);
  }, [booking.date, recurrence]);

  if (!recurrence || booking.isCancelled || !canManage) return null;

  const handleSubmit = async (): Promise<void> => {
    if (!openEntry) return;
    const action: "add" | "remove" = openEntry.state === "excluded" ? "remove" : "add";
    await mutation.mutateAsync({
      bookingId: baseBooking.id,
      date: openEntry.iso,
      action,
      waive: action === "add" && isElevated && waive,
    });
    onMutated?.();
    setOpenEntry(null);
    setWaive(false);
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    mutation.reset();
    setOpenEntry(null);
    setWaive(false);
  };

  return (
    <div
      className="rounded-md border border-gray-200 bg-gray-100 shadow"
      data-testid="recurrence-exceptions-manager"
    >
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CollapsibleTrigger className="w-full flex items-center justify-between gap-3 p-4 text-left">
          <div>
            <p className="text-base font-semibold">Manage recurrence dates</p>
            <p className="text-sm text-gray-500">
              Exclude a single date from this recurring booking. Excluded dates free up the room.
            </p>
          </div>
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ScrollArea className="max-h-[60vh] overflow-y-auto border-t border-gray-100">
            <ul className="divide-y divide-gray-100">
              {entries.map((entry) => {
                const isPast = entry.state === "past";
                return (
                  <li key={entry.iso}>
                    <button
                      type="button"
                      disabled={isPast || mutation.isPending}
                      onClick={() => {
                        setWaive(false);
                        setOpenEntry(entry);
                      }}
                      className={cn(
                        "w-full flex items-center justify-between gap-4 px-4 py-3 text-left transition-colors",
                        isPast
                          ? "cursor-not-allowed text-gray-400"
                          : "hover:bg-indigo-50",
                        entry.state === "excluded" && "text-gray-500",
                      )}
                      data-testid={`recurrence-row-${entry.iso}`}
                      data-state={entry.state}
                    >
                      <span
                        className={cn(
                          "text-sm",
                          entry.state === "excluded" && "line-through",
                        )}
                      >
                        {format(entry.date, "EEEE d MMMM yyyy")}
                      </span>
                      <span className="flex items-center gap-2">
                        {entry.state === "excluded" && <Undo2 className="h-4 w-4 text-amber-700" />}
                        {entry.state === "available" && <CalendarOff className="h-4 w-4 text-emerald-700" />}
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            stateBadgeClasses[entry.state],
                          )}
                        >
                          {stateLabel[entry.state]}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {entries.length === 0 && (
              <p className="p-4 text-sm text-gray-500">No recurrence instances to display.</p>
            )}
          </ScrollArea>
          <div className="flex justify-end border-t border-gray-100 p-3">
            <Button variant="outline" size="sm" disabled>
              {entries.length} instance{entries.length === 1 ? "" : "s"} shown
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {openEntry && (
        <RecurrenceExceptionConfirmDialog
          entry={openEntry}
          open={openEntry !== null}
          pending={mutation.isPending}
          error={mutation.error}
          willBeCharged={willBeCharged}
          canWaive={isElevated}
          waive={waive}
          onWaiveChange={setWaive}
          onConfirm={handleSubmit}
          onClose={handleClose}
        />
      )}
    </div>
  );
}

export default RecurrenceExceptionsManager;
